import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const UserAuthContext = createContext(null);

export function UserAuthProvider({ children }) {
  const [user, setUser] = useState(null); // null checking, false guest, object user
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me-user");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Emergent OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!user) {
      setFavorites([]);
      return;
    }
    api.get("/me/favorites").then((r) => setFavorites(r.data.favorites || [])).catch(() => {});
  }, [user]);

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post("/auth/google/logout");
    } catch {}
    setUser(false);
  };

  const toggleFavorite = async (slug) => {
    if (!user) {
      toast.info("Sign in with Google to save favorite programs.");
      return;
    }
    const isFav = favorites.includes(slug);
    const next = isFav ? favorites.filter((s) => s !== slug) : [...favorites, slug];
    setFavorites(next);
    try {
      if (isFav) {
        await api.delete(`/me/favorites/${slug}`);
      } else {
        await api.put(`/me/favorites/${slug}`);
        toast.success("Saved to your programs.");
      }
    } catch {
      setFavorites(favorites);
      toast.error("Could not update favorites right now.");
    }
  };

  return (
    <UserAuthContext.Provider value={{ user, loading, loginWithGoogle, logout, refresh: checkAuth, setUser, favorites, toggleFavorite }}>
      {children}
    </UserAuthContext.Provider>
  );
}

export const useUserAuth = () => useContext(UserAuthContext);
